/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionFail,
  ActionSuccess,
  AssetUtils,
  Ducks,
  Menu,
  MenuItemType,
  MenuUiArea,
  Recording,
  RecordingStatus,
  RecordingsUtils,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, takeLeading, call, take, race } from 'redux-saga/effects';
import {
  Selectors as SingleRecordingsSelectors,
  ActionType as SingleRecordingsActionTypes,
} from 'core/store/ducks/recordings/singleRecordings';
import { StoreState } from 'store/store';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';

enum ActionType {
  UPDATE_RECORDINGS = 'myTv/recordings/UPDATE',
  FAIL_RECORDINGS = 'myTv/recordings/FAIL',
}

interface Recordings {
  recordings: Recording[];
  scheduledRecordings: Recording[];
}

type UpdateRecordings = ActionSuccess<ActionType.UPDATE_RECORDINGS, Recordings>;
type FailRecordings = ActionFail<ActionType.FAIL_RECORDINGS>;

export type Actions = UpdateRecordings | FailRecordings;

const Actions = {
  updateRecordings: (recordings: Recording[], scheduledRecordings: Recording[]): UpdateRecordings => ({
    type: ActionType.UPDATE_RECORDINGS,
    payload: {
      recordings,
      scheduledRecordings,
    },
  }),
  failRecordings: (error: XError): FailRecordings => ({
    type: ActionType.FAIL_RECORDINGS,
    error,
  }),
};

export interface ReducerState {
  processed?: boolean;
  data: Recordings;
  error?: XError;
}

const initialState: ReducerState = {
  data: {
    recordings: [],
    scheduledRecordings: [],
  },
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.UPDATE_RECORDINGS:
      return {
        data: action.payload,
        processed: true,
        error: undefined,
      };
    case ActionType.FAIL_RECORDINGS:
      return {
        data: initialState.data,
        processed: false,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getRecordings: (state: StoreState): Recording[] => state.myTv.recordings.data.recordings,
  getScheduledRecordings: (state: StoreState): Recording[] => state.myTv.recordings.data.scheduledRecordings,
  isRecordingsProcessed: (state: StoreState): boolean => state.myTv.recordings.processed || false,
  getError: (state: StoreState): XError | undefined => state.myTv.recordings.error,
};

/* Helping functions */

function* hasScheduleRail(): SagaIterator<boolean> {
  const menu: Menu | undefined = yield select(Ducks.Menu.Selectors.getMenu);
  const myTVMenu = menu && menu[MenuUiArea.MY_TV];
  return !!myTVMenu?.children.find((child) => child.type === MenuItemType.SCHEDULE_RECORDINGS);
}

function* updateRecordingsSaga(): SagaIterator {
  const allRecordings: Recording[] = yield select(SingleRecordingsSelectors.getRecordings);

  const sortedRecordings = RecordingsUtils.sort(allRecordings);

  let recordings = [...sortedRecordings];

  // if the schedule recordings rail is present in the menu, remove the schedule records and present them in the separated rail
  const scheduleRail: boolean = yield call(hasScheduleRail);
  if (scheduleRail) {
    recordings = recordings.filter(
      (rec) =>
        RecordingsUtils.getRecordingStatus(rec) === RecordingStatus.COMPLETED ||
        RecordingsUtils.getRecordingStatus(rec) === RecordingStatus.ON_GOING
    );
  }

  recordings = AssetUtils.merge(recordings);

  recordings = yield call(
    RailBadgeEngine.getAssetsWithBadgesListFromRail,
    recordings,
    BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RECORDING_RAIL
  );

  let scheduled = sortedRecordings.filter(
    (rec) => RecordingsUtils.getRecordingStatus(rec) === RecordingStatus.SCHEDULED
  );

  scheduled = AssetUtils.merge(scheduled);

  scheduled = yield call(
    RailBadgeEngine.getAssetsWithBadgesListFromRail,
    scheduled,
    BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RECORDING_RAIL
  );

  yield put(Actions.updateRecordings(recordings, scheduled));
}

function* failRecordingsSaga(action: ActionFail<SingleRecordingsActionTypes.FAIL>): SagaIterator {
  yield put(Actions.failRecordings(action.error));
}

export function* waitForRecordings(): SagaIterator<boolean> {
  if (yield select(Selectors.isRecordingsProcessed)) return true;

  const { error } = yield race({
    success: take(ActionType.UPDATE_RECORDINGS),
    error: take(ActionType.FAIL_RECORDINGS),
  });
  return error === undefined;
}

export function* saga(): SagaIterator {
  yield all([
    takeLeading(SingleRecordingsActionTypes.UPDATE, updateRecordingsSaga),
    takeLeading(SingleRecordingsActionTypes.FAIL, failRecordingsSaga),
  ]);
}
